import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faDollarSign, 
  faBed, 
  faBath,
  faMapMarkerAlt,
  faCalendar,
  faClock,
  faSave,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import axiosInstance from '../../helpers/api';

interface BuyerProfileModalProps {
  show: boolean;
  onHide: () => void;
  leadId: string;
  leadName: string;
  existingProfile?: any;
  onSave: () => void;
}

const BuyerProfileModal: React.FC<BuyerProfileModalProps> = ({
  show,
  onHide,
  leadId,
  leadName,
  existingProfile,
  onSave
}) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Property preferences
    property_type: '',
    bedrooms: '',
    bathrooms: '',
    budget_min: '',
    budget_max: '',
    square_feet_min: '',
    square_feet_max: '',
    
    // Location preferences
    preferred_cities: '',
    work_location: '',
    max_commute_time: '',
    
    // Timeline and status
    buying_timeframe: '',
    desired_move_in_date: '',
    pre_approved: 'false',
    pre_approval_amount: '',
    first_time_buyer: 'false',
    
    // Additional info
    motivation: '',
    buyer_stage: 'researching',
    additional_notes: ''
  });

  useEffect(() => {
    if (existingProfile) {
      const prefs = existingProfile.property_preferences || {};
      const locPrefs = existingProfile.location_preferences || {};
      
      setFormData({
        property_type: prefs.property_type || '',
        bedrooms: prefs.bedrooms?.toString() || '',
        bathrooms: prefs.bathrooms?.toString() || '',
        budget_min: prefs.budget_min?.toString() || '',
        budget_max: prefs.budget_max?.toString() || '',
        square_feet_min: prefs.square_feet_min?.toString() || '',
        square_feet_max: prefs.square_feet_max?.toString() || '',
        preferred_cities: locPrefs.preferred_cities?.join(', ') || '',
        work_location: existingProfile.work_location || '',
        max_commute_time: existingProfile.max_commute_time?.toString() || '',
        buying_timeframe: existingProfile.buying_timeframe || '',
        desired_move_in_date: existingProfile.desired_move_in_date || '',
        pre_approved: existingProfile.pre_approved ? 'true' : 'false',
        pre_approval_amount: existingProfile.pre_approval_amount?.toString() || '',
        first_time_buyer: existingProfile.first_time_buyer ? 'true' : 'false',
        motivation: existingProfile.motivation || '',
        buyer_stage: existingProfile.buyer_stage || 'researching',
        additional_notes: existingProfile.lifestyle_preferences?.deal_breakers?.join(', ') || ''
      });
    }
  }, [existingProfile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        property_preferences: {
          property_type: formData.property_type,
          bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
          bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
          budget_min: formData.budget_min ? parseInt(formData.budget_min) : null,
          budget_max: formData.budget_max ? parseInt(formData.budget_max) : null,
          square_feet_min: formData.square_feet_min ? parseInt(formData.square_feet_min) : null,
          square_feet_max: formData.square_feet_max ? parseInt(formData.square_feet_max) : null,
        },
        location_preferences: {
          preferred_cities: formData.preferred_cities.split(',').map(s => s.trim()).filter(s => s)
        },
        lifestyle_preferences: {
          deal_breakers: formData.additional_notes.split(',').map(s => s.trim()).filter(s => s)
        },
        buying_timeframe: formData.buying_timeframe,
        desired_move_in_date: formData.desired_move_in_date,
        pre_approved: formData.pre_approved === 'true',
        pre_approval_amount: formData.pre_approval_amount ? parseInt(formData.pre_approval_amount) : null,
        first_time_buyer: formData.first_time_buyer === 'true',
        motivation: formData.motivation,
        buyer_stage: formData.buyer_stage,
        work_location: formData.work_location,
        max_commute_time: formData.max_commute_time ? parseInt(formData.max_commute_time) : null
      };

      let response;
      if (existingProfile?.id) {
        // Update existing profile using lead ID
        response = await axiosInstance.put(`/buyer-profiles/lead/${leadId}`, payload);
      } else {
        // Create new profile using lead ID
        response = await axiosInstance.post(`/buyer-profiles/lead/${leadId}`, payload);
      }

      if (response.data.success) {
        toast.success(`Buyer profile ${existingProfile ? 'updated' : 'created'} successfully!`);
        onSave();
        onHide();
      } else {
        // If response doesn't have success field, still close modal on 200/201 status
        if (response.status === 200 || response.status === 201) {
          toast.success(`Buyer profile ${existingProfile ? 'updated' : 'created'} successfully!`);
          onSave();
          onHide();
        }
      }
    } catch (error: any) {
      console.error('Error saving buyer profile:', error);
      toast.error(error.response?.data?.message || 'Failed to save buyer profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered 
      style={{ zIndex: 9999 }}
      backdrop="static"
      backdropClassName="high-z-backdrop"
    >
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FontAwesomeIcon icon={faUser} className="me-2" />
          {existingProfile ? 'Edit' : 'Create'} Buyer Profile - {leadName}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="info">
            <strong>Complete Buyer Profile</strong>
            <p className="mb-0 mt-1">
              This information will be used to personalize the AI portal experience for your buyer.
            </p>
          </Alert>

          {/* Property Preferences Section */}
          <h5 className="mb-3 mt-4">
            <FontAwesomeIcon icon={faHome} className="me-2 text-primary" />
            Property Preferences
          </h5>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Property Type</Form.Label>
                <Form.Select
                  value={formData.property_type}
                  onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                >
                  <option value="">Select Type</option>
                  <option value="single_family">Single Family Home</option>
                  <option value="condo">Condominium</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="multi_family">Multi-Family</option>
                  <option value="land">Land/Lot</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faBed} className="me-1" />
                  Bedrooms
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 3"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faBath} className="me-1" />
                  Bathrooms
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 2"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({...formData, bathrooms: e.target.value})}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                  Budget Range
                </Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="number"
                    placeholder="Min"
                    value={formData.budget_min}
                    onChange={(e) => setFormData({...formData, budget_min: e.target.value})}
                  />
                  <span className="align-self-center">to</span>
                  <Form.Control
                    type="number"
                    placeholder="Max"
                    value={formData.budget_max}
                    onChange={(e) => setFormData({...formData, budget_max: e.target.value})}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Square Footage</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="number"
                    placeholder="Min sq ft"
                    value={formData.square_feet_min}
                    onChange={(e) => setFormData({...formData, square_feet_min: e.target.value})}
                  />
                  <span className="align-self-center">to</span>
                  <Form.Control
                    type="number"
                    placeholder="Max sq ft"
                    value={formData.square_feet_max}
                    onChange={(e) => setFormData({...formData, square_feet_max: e.target.value})}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          {/* Location Preferences */}
          <h5 className="mb-3 mt-4">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-primary" />
            Location Preferences
          </h5>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Preferred Cities/Areas</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Austin, Round Rock, Cedar Park"
                  value={formData.preferred_cities}
                  onChange={(e) => setFormData({...formData, preferred_cities: e.target.value})}
                />
                <Form.Text className="text-muted">
                  Separate multiple cities with commas
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Work Location</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Downtown Austin"
                  value={formData.work_location}
                  onChange={(e) => setFormData({...formData, work_location: e.target.value})}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faClock} className="me-1" />
                  Max Commute Time (minutes)
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g., 30"
                  value={formData.max_commute_time}
                  onChange={(e) => setFormData({...formData, max_commute_time: e.target.value})}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Timeline and Status */}
          <h5 className="mb-3 mt-4">
            <FontAwesomeIcon icon={faCalendar} className="me-2 text-primary" />
            Timeline & Status
          </h5>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Buying Timeline</Form.Label>
                <Form.Select
                  value={formData.buying_timeframe}
                  onChange={(e) => setFormData({...formData, buying_timeframe: e.target.value})}
                >
                  <option value="">Select Timeline</option>
                  <option value="asap">ASAP</option>
                  <option value="1_month">Within 1 month</option>
                  <option value="3_months">1-3 months</option>
                  <option value="6_months">3-6 months</option>
                  <option value="12_months">6-12 months</option>
                  <option value="over_12_months">Over 12 months</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Desired Move-in Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.desired_move_in_date}
                  onChange={(e) => setFormData({...formData, desired_move_in_date: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Buyer Stage</Form.Label>
                <Form.Select
                  value={formData.buyer_stage}
                  onChange={(e) => setFormData({...formData, buyer_stage: e.target.value})}
                >
                  <option value="researching">Researching</option>
                  <option value="pre_approved">Pre-approved</option>
                  <option value="actively_looking">Actively Looking</option>
                  <option value="making_offers">Making Offers</option>
                  <option value="under_contract">Under Contract</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Pre-approved?</Form.Label>
                <Form.Select
                  value={formData.pre_approved}
                  onChange={(e) => setFormData({...formData, pre_approved: e.target.value})}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </Form.Select>
              </Form.Group>
            </Col>
            {formData.pre_approved === 'true' && (
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Pre-approval Amount</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 500000"
                    value={formData.pre_approval_amount}
                    onChange={(e) => setFormData({...formData, pre_approval_amount: e.target.value})}
                  />
                </Form.Group>
              </Col>
            )}
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>First-time Buyer?</Form.Label>
                <Form.Select
                  value={formData.first_time_buyer}
                  onChange={(e) => setFormData({...formData, first_time_buyer: e.target.value})}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Additional Info */}
          <h5 className="mb-3 mt-4">Additional Information</h5>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Motivation for Buying</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g., Growing family, investment opportunity, downsizing..."
                  value={formData.motivation}
                  onChange={(e) => setFormData({...formData, motivation: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Deal Breakers / Must-Haves</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g., No HOA, Must have pool, Need home office..."
                  value={formData.additional_notes}
                  onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                />
                <Form.Text className="text-muted">
                  Separate items with commas
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Save Buyer Profile
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BuyerProfileModal;